import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, CalendarClock, Layers, Briefcase, FileText, FileEdit, Scale, Mail, Users, Settings, LogOut, ExternalLink, FolderKanban, TrendingUp, Bell, BellOff } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "admin-nav-overview" },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban, testid: "admin-nav-projects" },
  { to: "/admin/leads", label: "Leads", icon: TrendingUp, testid: "admin-nav-leads" },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, testid: "admin-nav-enquiries" },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarClock, testid: "admin-nav-bookings" },
  { to: "/admin/services", label: "Services", icon: Layers, testid: "admin-nav-services" },
  { to: "/admin/portfolio", label: "Portfolio", icon: Briefcase, testid: "admin-nav-portfolio" },
  { to: "/admin/posts", label: "Articles", icon: FileText, testid: "admin-nav-posts" },
  { to: "/admin/content", label: "Content", icon: FileEdit, testid: "admin-nav-content" },
  { to: "/admin/legal", label: "Legal", icon: Scale, testid: "admin-nav-legal" },
  { to: "/admin/templates", label: "Emails", icon: Mail, testid: "admin-nav-templates" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login", { replace: true, state: { from: location.pathname } }))
      .finally(() => setChecking(false));
  }, [navigate, location.pathname]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    navigate("/admin/login");
  };

  const [pushOn, setPushOn] = useState(false);
  const pushSupported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
  useEffect(() => {
    if (!pushSupported) return;
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => setPushOn(!!sub));
    });
  }, [pushSupported]);
  const togglePush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushOn) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
        setPushOn(false);
      } else {
        const { publicKey } = (await api.get("/pms/push/vapid-key")).data;
        const pad = "=".repeat((4 - (publicKey.length % 4)) % 4);
        const b64 = (publicKey + pad).replace(/-/g, "+").replace(/_/g, "/");
        const raw = window.atob(b64);
        const key = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) key[i] = raw.charCodeAt(i);
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
        await api.post("/pms/push/subscribe", sub.toJSON());
        setPushOn(true);
      }
    } catch (e) { console.warn("Push toggle failed:", e); }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-black" data-testid="admin-auth-checking">
        <p className="text-sm text-brand-mist">Checking access…</p>
      </div>
    );
  }
  if (!user) return null;

  // Role-aware navigation: admin sees everything; editor sees content tools;
  // member sees only the Projects (PMS) module. Enforced server-side too.
  const ALLOWED_BY_ROLE = {
    admin: null, // null = full nav
    editor: ["portfolio", "posts", "content"],
    member: ["projects"],
    sales: ["leads"],
  };
  const allowed = Object.prototype.hasOwnProperty.call(ALLOWED_BY_ROLE, user.role)
    ? ALLOWED_BY_ROLE[user.role]
    : ["projects"];
  const visibleNav = allowed === null ? NAV : NAV.filter((n) => allowed.some((k) => n.to.endsWith(k)));

  return (
    <div className="flex min-h-screen bg-brand-black" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 flex w-16 flex-col border-r border-brand-mist/10 bg-brand-charcoal lg:w-60">
        <div className="flex h-16 items-center justify-center border-b border-brand-mist/10 px-4 lg:justify-start">
          <Logo compact />
          <span className="ml-2.5 hidden font-display text-sm font-semibold text-brand-snow lg:inline">Admin</span>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Admin">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive ? "bg-brand-emerald/10 text-brand-emerald" : "text-brand-mist hover:bg-white/5 hover:text-brand-snow"
                )
              }
            >
              <item.icon size={17} className="shrink-0" />
              <span className="hidden lg:inline">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 border-t border-brand-mist/10 p-3">
          {pushSupported && (
            <button onClick={togglePush} data-testid="admin-push-toggle" aria-label={pushOn ? "Disable notifications" : "Enable notifications"} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-brand-mist transition-colors hover:bg-white/5 hover:text-brand-snow">
              {pushOn ? <Bell size={17} className="shrink-0 text-brand-emerald" /> : <BellOff size={17} className="shrink-0" />}
              <span className="hidden lg:inline">{pushOn ? "Notifications on" : "Enable notifications"}</span>
            </button>
          )}
          <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-brand-mist transition-colors hover:bg-white/5 hover:text-brand-snow">
            <ExternalLink size={17} className="shrink-0" />
            <span className="hidden lg:inline">View website</span>
          </a>
          <button onClick={logout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-brand-mist transition-colors hover:bg-white/5 hover:text-red-400">
            <LogOut size={17} className="shrink-0" />
            <span className="hidden lg:inline">Sign out</span>
          </button>
        </div>
      </aside>
      <main className="ml-16 flex-1 p-5 sm:p-8 lg:ml-60 lg:p-10">
        <Outlet context={{ user }} />
      </main>
    </div>
  );
};

export default AdminLayout;
